import { Injectable } from '@angular/core';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { filter, take } from 'rxjs';
import _ from 'lodash';

import { CommonConstant, TicketConstant } from 'src/app/constants';
import { GeneralHelper } from 'src/app/helpers';
import {
  BaseMenuItem,
  LocationType,
  MenuItemType,
  NormalizedTicket,
  PriceType,
  ProductType,
  TakeoutOrderStatus,
  Ticket,
  TicketItem,
  TicketItemRequest,
  TicketItemType
} from 'src/app/models';
import {
  ModifierPromptOption,
  ModifierPromptOptionType
} from 'src/app/models/pos-manager.model';
import { LOCATION_STATE_TOKEN } from 'src/app/store/location/location.state';
import { LocationStateHelper } from 'src/app/store/location/location.state.helper';
import { MENU_STATE_TOKEN } from 'src/app/store/menu/menu.state.model';
import { TAKEOUT_DELIVERY_STATE_TOKEN } from 'src/app/store/takeout-delivery/takeout-delivery.state';
import {
  AddTicketItem,
  AddTicketItemWithPrompts
} from 'src/app/store/ticket-items/ticket-items.action';
import { CreateTicket } from 'src/app/store/tickets/tickets.action';
import { TICKETS_STATE_TOKEN } from 'src/app/store/tickets/tickets.state';
import { TicketService } from 'src/app/services';

@Injectable({
  providedIn: 'root'
})
export class TicketProvider {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly ticketService: TicketService
  ) {}

  ticketItemAddingHandler(params: {
    menuItem: BaseMenuItem;
    ticket_uuid: string;
    ticket_item_uuid?: string;
  }): void {
    const { menuItem, ticket_item_uuid, ticket_uuid } = params;
    const { byId: ticketsById } =
      this.store.selectSnapshot(TICKETS_STATE_TOKEN).tickets;
    const ticket = ticketsById[ticket_uuid];

    // Ticket has been created
    if (ticket?.status) {
      this.ticketItemAddHandler(menuItem, ticket_item_uuid);
      return;
    }

    // If ticket is being created, wait for ticket creation to complete
    this.actions$
      .pipe(
        ofActionCompleted(CreateTicket),
        take(1),
        filter(({ result }) => !result.error)
      )
      .subscribe(() => this.ticketItemAddHandler(menuItem, ticket_item_uuid));

    if (!this._isTicketCreating) {
      this.store.dispatch(new CreateTicket());
    }
  }

  ticketItemAddingWithPrompts(
    menuItem: BaseMenuItem,
    options: ModifierPromptOption[]
  ) {
    if (!this.selectedTicket?.status) {
      this.store.dispatch(new CreateTicket()).subscribe(() => {
        this.ticketItemAddWithPromptsHandler(menuItem, options);
      });
    } else {
      this.ticketItemAddWithPromptsHandler(menuItem, options);
    }
  }

  canAddTicketItem(
    ticket: Ticket | NormalizedTicket,
    isTakeOutTicket: boolean
  ) {
    return (
      !ticket?.status ||
      TicketConstant.ABLE_ADD_NEW_ITEM_STATUS.includes(ticket.status) ||
      // POSV3-1303 Allow add ticket/ modifier for scheduled/unpaid
      (isTakeOutTicket &&
        ticket.takeout_order_status === TakeoutOrderStatus.SCHEDULED &&
        !ticket.takeout_order_payment_status)
    );
  }

  ticketItemDuplicateHandler(ticketItem: TicketItem) {
    const item = this.duplicateTicketItem(ticketItem);
    this.store.dispatch(new AddTicketItem(item));
  }

  private ticketItemAddWithPromptsHandler(
    menuItem: BaseMenuItem,
    options: ModifierPromptOption[]
  ) {
    menuItem.quantity = menuItem.quantity || 1;
    const item = this.initNewTicketItem(menuItem, null);
    item.children_attributes = options.map((option) =>
      this.initNewTicketItemForModifierPrompt(item, option)
    );

    this.store.dispatch(
      new AddTicketItemWithPrompts(item, this.selectedTicket?.can_be_cancelled)
    );
    menuItem.quantity = 1;
  }

  private ticketItemAddHandler(
    menuItem: BaseMenuItem,
    device_ticket_item_uuid?: string
  ) {
    menuItem.quantity = menuItem.quantity || 1;
    const item = this.initNewTicketItem(menuItem, device_ticket_item_uuid);
    this.store.dispatch(
      new AddTicketItem(item, this.selectedTicket?.can_be_cancelled)
    );
    menuItem.quantity = 1;
  }

  private initNewTicketItemForModifierPrompt(
    parentRequest: TicketItemRequest,
    option: ModifierPromptOption
  ): TicketItemRequest {
    const item: TicketItemRequest = {
      course: parentRequest.course,
      base_price: +option.added_price || 0,
      name:
        option.text_label || option.pos_product?.name || option.category?.name,
      name_translations: option.text_label
        ? option.text_label_translations
        : option.pos_product?.name_translations ??
          option.category?.name_translations,
      quantity: 1,
      amount: option.added_price
        ? GeneralHelper.asDecimal(option.added_price)
        : 0,
      category_id: option.category?.id,
      kitchen_quantity:
        option.fullMenuItem?.amount && option.fullMenuItem?.unit
          ? `${option.fullMenuItem.amount} ${option.fullMenuItem.unit}`
          : '1 Each',
      type: TicketItemType.MODIFIER,
      modifier_color: option.modifier_color,
      recipe_cost: option.fullMenuItem?.recipe_cost ?? null // snapshot modifier food cost
    };

    if (
      option.option_type === ModifierPromptOptionType.Category
      // ModifierPromptOptionType.CategoryChildItem
    ) {
      // this logic base on the add normal menu item (method initNewTicketItem)
      switch (option.fullMenuItem?.type) {
        case MenuItemType.Product:
          item.pos_product_id = option.fullMenuItem?.id;
          item.kitchen_quantity = option.fullMenuItem.unit;
          break;
        case MenuItemType.Food:
          item.food_id = option.fullMenuItem?.food_id;
          break;
        case MenuItemType.Plate:
          item.plate_id = option.fullMenuItem?.id;
          break;
      }
    } else if (option.option_type === ModifierPromptOptionType.PosProduct) {
      item.pos_product_id = option.pos_product?.id;
      item.kitchen_quantity =
        option.fullMenuItem?.unit || item.kitchen_quantity;
    }

    item.original_quantity = item.kitchen_quantity;

    return item;
  }

  private initNewTicketItem(
    menuItem: BaseMenuItem,
    device_ticket_item_uuid?: string
  ): TicketItemRequest {
    const location = this.store.selectSnapshot(LOCATION_STATE_TOKEN).location;
    const locationType = location.type;
    const isCoursingEnabled: boolean =
      LocationStateHelper.getIsCoursingEnabled(location);
    const isDiningRoom: boolean = locationType === LocationType.DiningRoom;
    const isTakeoutDelivery = this.store.selectSnapshot(
      TAKEOUT_DELIVERY_STATE_TOKEN
    ).enableTakeOutDelivery;

    // Determine course
    let course = null; // Replace 0 with null when DEF-3841 is fixed on backend
    // Set item course if coursing enabled for dining room locations only and not on takeout/delivery
    if (!isTakeoutDelivery && isDiningRoom && isCoursingEnabled) {
      const courseByCategory = this.store
        .selectSnapshot(MENU_STATE_TOKEN)
        .menu.categories.find((cat) => cat.id === menuItem.category_id)?.course;
      course =
        courseByCategory ||
        (CommonConstant.ALL_COURSES.includes(menuItem.course)
          ? menuItem.course
          : course);
    }

    // Setup ticket item details
    const item: TicketItemRequest = {
      course,
      base_price: menuItem.price || 0,
      name: menuItem.name,
      name_translations: menuItem.name_translations,
      quantity: menuItem.quantity,
      amount: GeneralHelper.asDecimal(menuItem.price),
      category_id: menuItem.category_id,
      recipe_cost: menuItem.recipe_cost ?? null, // snapshot per-unit food cost; covers Food/Product/Plate
      kitchen_quantity:
        menuItem.amount && menuItem.unit
          ? `${menuItem.amount} ${menuItem.unit}`
          : '1 Each'
    };

    if (item.base_price && item.base_price.toString().endsWith('.')) {
      item.base_price = Math.round(item.base_price);
    }

    if (menuItem.type === MenuItemType.Product) {
      item.pos_product_id = menuItem.id;
      item.type = TicketItemType.PRODUCT;
      item.kitchen_quantity = menuItem.unit;
      if (menuItem.product_type === ProductType.GIFT_CARD) {
        item.type = TicketItemType.GIFT_CARD;
        item.gift_card_code = menuItem.gift_card_code;
        item.gift_card_id = menuItem.gift_card_id;
      }
    } else if (menuItem.type === MenuItemType.Food) {
      item.food_id = menuItem.food_id;
      item.type = TicketItemType.FOOD;
    } else if (menuItem.type === MenuItemType.Plate) {
      item.plate_id = menuItem.id;
      item.type = TicketItemType.PLATE;
    } else {
      item.type = TicketItemType.MODIFIER;
      item.parent_uuid = device_ticket_item_uuid;
    }

    item.original_quantity = item.kitchen_quantity;
    item.modifier_color = menuItem.modifier_color;

    if (device_ticket_item_uuid) {
      // this item is added as modifier item
      item.parent_uuid = device_ticket_item_uuid;
    }

    if (
      menuItem.price_type &&
      menuItem.price_type !== PriceType.FIXED &&
      menuItem.price_type !== PriceType.VARIABLE
    ) {
      const scaleData: any = _.cloneDeep(menuItem.scale);
      item.recipe_cost = null; // weighted/scale items: server returns N/A, so send null
      item.base_price = scaleData.item_price_by_weight;
      scaleData.weight = scaleData.weight_without_container;
      scaleData.item_price_by_weight = true;
      if (_.has(scaleData, 'weight_without_container')) {
        delete scaleData.weight_without_container;
      }
      item.scale_data = JSON.stringify(scaleData).replace(/"/g, '\\"');
    }

    return item;
  }

  private duplicateTicketItem(
    ticketItem: TicketItem,
    uuid?: string
  ): TicketItemRequest {
    const device_ticket_item_uuid =
      this.ticketService.generateTicketOrTicketItemUuid('TICKET_ITEM');

    const item: TicketItemRequest = {
      device_ticket_item_uuid,
      course: ticketItem.course,
      base_price: ticketItem.base_price,
      name: ticketItem.name,
      name_translations: ticketItem.name_translations,
      quantity: 1,
      amount: GeneralHelper.asDecimal(ticketItem.base_price),
      category_id: ticketItem.category_id,
      type: ticketItem.type,
      pos_product_id: ticketItem.pos_product_id,
      kitchen_quantity: ticketItem.kitchen_quantity,
      original_quantity: ticketItem.kitchen_quantity,
      modifier_color: ticketItem.modifier_color,
      food_id: ticketItem.food_id,
      plate_id: ticketItem.plate_id,
      recipe_cost: ticketItem.recipe_cost ?? null, // carry persisted snapshot on duplicate
      scale_data: ticketItem.scale_data,
      covered_by_parent_item_category:
        ticketItem.covered_by_parent_item_category
    };

    if (ticketItem.scale_data) {
      item.scale_data = ticketItem.scale_data;
    }

    if (ticketItem.gift_card_data) {
      item.gift_card_code = ticketItem.gift_card_data.number;
    }

    if (uuid) {
      item.parent_uuid = uuid;
    }

    if (ticketItem.ticket_items) {
      item.children_attributes = ticketItem.ticket_items.map((i) =>
        this.duplicateTicketItem(i, device_ticket_item_uuid)
      );
    }

    return item;
  }

  private get selectedTicket(): NormalizedTicket {
    const ticketsState = this.store.selectSnapshot(TICKETS_STATE_TOKEN);
    const ticketUuid = ticketsState.selectedTicket.data;
    const ticketsById = ticketsState.tickets.byId;
    return !!ticketUuid && !!ticketsById ? ticketsById[ticketUuid] : null;
  }

  private get _isTicketCreating(): boolean {
    return this.store.selectSnapshot(TICKETS_STATE_TOKEN).selectedTicket
      .isProcessing;
  }
}
